import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import WebApp from "@twa-dev/sdk";
import type { User, Subscription, Achievement, ContinueLesson, Badges } from "./types";
import {
  loginTelegram, loginFirebase as apiLoginFirebase,
  fetchMe, fetchSubscription, fetchNotifications, ackNotifications, fetchContinueLesson, fetchBadges,
  redeemInvite, redeemHandoff, peekHandoff, fetchBepaidStatus,
} from "./utils/api";
import AchievementOverlay from "./components/common/Achievement";
import LoginView from "./components/auth/LoginView";
import ApproveLogin from "./components/auth/ApproveLogin";
import MainMenu from "./components/menu/MainMenu";
import SelectionWizard, { type SelectionWizardCache } from "./components/selection/SelectionWizard";
import StudyView from "./components/study/StudyView";
import AccountView from "./components/account/AccountView";
import HistoryView from "./components/history/HistoryView";
import GamesHub from "./components/games/GamesHub";
import ChatScreen from "./components/chat/ChatScreen";
import AdminChats from "./components/admin/AdminChats";
import AdminQuizStats from "./components/admin/AdminQuizStats";
import AdminTaskReview from "./components/admin/AdminTaskReview";
import AdminSettings from "./components/admin/AdminSettings";
import AdminInvites from "./components/admin/AdminInvites";
import AdminUsers from "./components/admin/AdminUsers";
import MaintenanceScreen from "./components/common/MaintenanceScreen";
import { AppConfigProvider, useAppConfig } from "./config/AppConfigContext";
import { captureInvite, clearInvite, inviteErrorCode } from "./utils/invite";

type View = "menu" | "selection" | "study" | "account" | "subscription" | "history" | "games" | "chat" | "adminchats" | "quizstats" | "taskreview" | "adminsettings" | "admininvites" | "adminusers";
type Theme = "dark" | "light";
type Device = "phone" | "tablet" | "desktop";

function initTheme(): Theme {
  try {
    const tg = (window as any).Telegram?.WebApp?.colorScheme;
    if (tg === "dark" || tg === "light") return tg;
  } catch {}
  const saved = localStorage.getItem("bee_theme");
  if (saved === "dark" || saved === "light") return saved as Theme;
  return window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
}

function BeeSpinner() {
  return (
    <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", position: "relative", zIndex: 1 }}>
      <div className="spinner spinner-amber" style={{ width: 32, height: 32, borderWidth: 3 }} />
    </div>
  );
}

/**
 * Blocks everyone but an admin while maintenance mode is on. Lives inside the
 * config provider (it needs the config) and only engages once the profile has
 * loaded, so an admin is never briefly locked out of their own app.
 */
function MaintenanceGate({
  ready, isAdmin, theme, onThemeChange, children,
}: {
  ready: boolean;
  isAdmin: boolean;
  theme: Theme;
  onThemeChange: (t: Theme) => void;
  children: React.ReactNode;
}) {
  const { config } = useAppConfig();
  if (ready && config.maintenance_mode && !isAdmin) {
    return <MaintenanceScreen theme={theme} onThemeChange={onThemeChange} />;
  }
  return <>{children}</>;
}

/**
 * "Pay in the browser" from the Telegram Mini App arrives as
 * /?handoff=<one-time token>&buy=<plan id>. Read ONCE per page load and
 * remembered: the address bar is cleaned on the first read, so a second call
 * (React may run state initialisers twice) must not see an empty URL and lose it.
 */
let handoffRead = false;
let handoffValue: { token: string; buy: string | null } | null = null;
let handoffRedeemStarted = false;
function readHandoffOnce(): { token: string; buy: string | null } | null {
  if (handoffRead) return handoffValue;
  handoffRead = true;
  try {
    const url = new URL(window.location.href);
    const tok = url.searchParams.get("handoff");
    if (tok) {
      handoffValue = { token: tok, buy: url.searchParams.get("buy") };
      url.searchParams.delete("handoff");
      url.searchParams.delete("buy");
      window.history.replaceState(window.history.state, "", url.pathname + url.search + url.hash);
    }
  } catch { /* malformed URL — no hand-off */ }
  return handoffValue;
}

/** bePaid sends the buyer back to /?bepaid=<payment id>. Read once, stripped. */
let bepaidRead = false;
let bepaidValue: string | null = null;
function readBepaidReturnOnce(): string | null {
  if (bepaidRead) return bepaidValue;
  bepaidRead = true;
  try {
    const url = new URL(window.location.href);
    bepaidValue = url.searchParams.get("bepaid");
    if (bepaidValue) {
      url.searchParams.delete("bepaid");
      window.history.replaceState(window.history.state, "", url.pathname + url.search + url.hash);
    }
  } catch { /* malformed URL */ }
  return bepaidValue;
}

/** The browser half of «Войти через Google» from the Mini App: /?approve=<code>.
 *  Parked in sessionStorage so it survives the Google/Apple sign-in first. */
function readApproveOnce(): string | null {
  try {
    const url = new URL(window.location.href);
    const code = url.searchParams.get("approve");
    if (code) {
      sessionStorage.setItem("bee_approve", code);
      url.searchParams.delete("approve");
      window.history.replaceState(window.history.state, "", url.pathname + url.search + url.hash);
    }
    return sessionStorage.getItem("bee_approve");
  } catch { return null; }
}

/** Signing out inside Telegram must stick: otherwise initData logs straight
 *  back into the Telegram account and Google could never be chosen. */
const LOGGED_OUT_KEY = "bee_tg_logged_out";
function tgLoggedOut(): boolean {
  try { return localStorage.getItem(LOGGED_OUT_KEY) === "1"; } catch { return false; }
}

export default function App() {
  const { t, i18n } = useTranslation();

  const [theme, setTheme] = useState<Theme>(initTheme);
  const [device, setDevice] = useState<Device>("phone");
  const [animOn, setAnimOn] = useState(true);
  const rootRef = useRef<HTMLDivElement>(null);

  const [token, setToken] = useState<string | null>(localStorage.getItem("token"));
  const [me, setMe] = useState<User | null>(null);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [view, setView] = useState<View>("menu");
  // Lesson → chat deep-link context, and which lesson to re-open on return.
  const [chatCtx, setChatCtx] = useState<{ subject: string; lessonId: string } | null>(null);
  const [studyOpenId, setStudyOpenId] = useState<string | null>(null);
  // Opening a lesson from the calculator unmounts the wizard, so its result is
  // cached here and restored on the way back (and back returns to it, not to
  // the lesson list).
  const [selCache, setSelCache] = useState<SelectionWizardCache | null>(null);
  // Where "back" from a deep-linked lesson should land (the screen that sent us
  // there — the calculator result or a saved one in history); null = the list.
  const [lessonReturnsTo, setLessonReturnsTo] = useState<View | null>(null);
  // Where the plans page should hand control back to — the calculator result or
  // the lessons list (with the chapter the user was browsing still open).
  const [accountReturnTo, setAccountReturnTo] = useState<View | null>(null);
  const [studyOpenSection, setStudyOpenSection] = useState<string | null>(null);
  const [continueLesson, setContinueLesson] = useState<ContinueLesson | null>(null);
  const [badges, setBadges] = useState<Badges | null>(null);
  // An invite link the app was opened with, waiting for the user to be signed
  // in. Read once at start-up; see utils/invite.ts.
  const [inviteCode, setInviteCode] = useState<string | null>(() => captureInvite());
  // "Pay in the browser" from the Telegram Mini App arrives as
  // /?handoff=<one-time token>&buy=<plan id>: sign in as that account, then
  // open the checkout for that plan. Read once, stripped from the address bar.
  const [handoff] = useState(readHandoffOnce);
  const [handoffPending, setHandoffPending] = useState(!!handoff);
  // Whose account the link opens. A link can come from anyone, so the visitor
  // confirms before their browser is switched to that account (security
  // audit 28.10.2026: a stranger's link silently signed people into the
  // stranger's account with checkout open).
  const [handoffWho, setHandoffWho] = useState<{ name: string; username: string | null } | null>(null);
  const [autoBuyPlanId, setAutoBuyPlanId] = useState<string | null>(handoff?.buy ?? null);
  const redeemingRef = useRef<string | null>(null);
  const [bepaidReturn, setBepaidReturn] = useState<string | null>(readBepaidReturnOnce);

  // Centre-screen achievement / "try again" pop-ups (queued, shown one at a time).
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const pushAchievement = useCallback((a: Achievement) => {
    setAchievements((prev) => (prev.some((x) => x.id === a.id) ? prev : [...prev, a]));
  }, []);
  const dismissAchievement = useCallback((id: string) => {
    setAchievements((prev) => prev.filter((x) => x.id !== id));
  }, []);

  useEffect(() => { document.title = t("app.title"); }, [t]);
  // The page language drives hyphenation (long words in narrow tiles break at
  // syllables, not mid-letter) and screen readers.
  useEffect(() => { document.documentElement.lang = i18n.language?.startsWith("en") ? "en" : "ru"; }, [i18n.language]);

  useEffect(() => { localStorage.setItem("bee_theme", theme); }, [theme]);

  // Device detection via ResizeObserver
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const obs = new ResizeObserver(([entry]) => {
      const w = entry.contentRect.width;
      setDevice(w >= 1024 ? "desktop" : w >= 768 ? "tablet" : "phone");
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // Entrance animation on screen change
  const animSig = `${!!token}:${!!me}:${view}`;
  useEffect(() => {
    setAnimOn(true);
    const id = setTimeout(() => setAnimOn(false), 1100);
    return () => clearTimeout(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animSig]);

  const [approveCode, setApproveCode] = useState<string | null>(readApproveOnce);
  const [loggedOut, setLoggedOut] = useState<boolean>(tgLoggedOut);
  const closeApprove = () => {
    try { sessionStorage.removeItem("bee_approve"); } catch { /* storage blocked */ }
    setApproveCode(null);
  };

  const persistToken = (tok: string) => {
    localStorage.setItem("token", tok);
    try { localStorage.removeItem(LOGGED_OUT_KEY); } catch { /* storage blocked */ }
    setLoggedOut(false);
    setToken(tok);
  };

  const logout = () => {
    if (WebApp.initData?.trim()) {
      try { localStorage.setItem(LOGGED_OUT_KEY, "1"); } catch { /* storage blocked */ }
      setLoggedOut(true);
    }
    localStorage.removeItem("token");
    setToken(null);
    setMe(null);
    setSubscription(null);
    setView("menu");
  };

  const handleLoginFirebase = async (idToken: string) => {
    const { access_token } = await apiLoginFirebase(idToken);
    persistToken(access_token);
  };

  const handleTelegramLogin = useCallback(async () => {
    const { access_token } = await loginTelegram(WebApp.initData);
    persistToken(access_token);
  }, []);

  const handoffExpired = () => {
    setAutoBuyPlanId(null);
    pushAchievement({ id: "handoff-expired", variant: "retry", title: t("handoff.expiredTitle"), message: t("handoff.expiredText") });
  };

  // First only look: whose account is this link for?
  useEffect(() => {
    if (!handoff || handoffRedeemStarted) return;
    peekHandoff(handoff.token)
      .then(setHandoffWho)
      .catch(() => { handoffExpired(); setHandoffPending(false); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const declineHandoff = () => {
    setHandoffWho(null);
    setAutoBuyPlanId(null);
    setHandoffPending(false);
  };

  const acceptHandoff = () => {
    // Once per page: a second redeem of the same one-time token would fail
    // and wrongly report the link as expired.
    if (!handoff || handoffRedeemStarted) return;
    handoffRedeemStarted = true;
    setHandoffWho(null);
    redeemHandoff(handoff.token)
      .then(({ access_token }) => {
        try { localStorage.setItem("bee_handoff", "1"); } catch { /* storage blocked */ }
        persistToken(access_token);
        setView("subscription");
      })
      .catch(handoffExpired)
      .finally(() => setHandoffPending(false));
  };

  // Back from bePaid: ask the server (which asks bePaid and opens access if
  // paid) a few times — the webhook usually lands within seconds anyway.
  useEffect(() => {
    if (!bepaidReturn || !token || !me) return;
    const id = bepaidReturn;
    setBepaidReturn(null);
    let tries = 0;
    let timer: ReturnType<typeof setTimeout>;
    const done = (variant: "success" | "retry" | "info", key: "Paid" | "Pending" | "Failed") => {
      pushAchievement({ id: `bepaid-${id}`, variant, title: t(`payMethod.return${key}Title`), message: t(`payMethod.return${key}Text`) });
      fetchSubscription(token).then(setSubscription).catch(() => {});
      setView("subscription");
    };
    const poll = () => {
      fetchBepaidStatus(id, token)
        .then((r) => {
          if (r.status === "completed") return done("success", "Paid");
          if (r.status === "failed") return done("retry", "Failed");
          if (++tries >= 8) return done("info", "Pending");
          timer = setTimeout(poll, 2500);
        })
        .catch(() => done("info", "Pending"));
    };
    poll();
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bepaidReturn, token, me]);

  // Telegram init + auto-login
  useEffect(() => {
    WebApp.ready();
    WebApp.expand();
    if (WebApp.initData?.trim() && !token && !loggedOut) handleTelegramLogin();
  }, [handleTelegramLogin, token, loggedOut]);

  // Load profile + subscription once we have a token
  useEffect(() => {
    if (!token) return;
    fetchMe(token).then(setMe).catch(() => {
      localStorage.removeItem("token");
      setToken(null);
    });
    fetchSubscription(token).then(setSubscription).catch(() => {
      setSubscription(null);
    });
  }, [token]);

  // Refresh subscription when returning to menu (in case admin granted one)
  useEffect(() => {
    if (view === "menu" && token) {
      fetchSubscription(token).then(setSubscription).catch(() => {});
    }
  }, [view, token]);

  // Refresh the "Continue watching" target whenever we land on the menu, so it
  // reflects the video position saved on the way out.
  useEffect(() => {
    if (view === "menu" && token) {
      fetchContinueLesson(token).then(setContinueLesson).catch(() => setContinueLesson(null));
      fetchBadges(token).then(setBadges).catch(() => setBadges(null));
    }
  }, [view, token]);

  // Pull pending grade pop-ups (work approved / sent back while the app was
  // closed). Runs on open and whenever we land back on the menu, then acks them
  // so each outcome is celebrated exactly once.
  const checkNotifications = useCallback(() => {
    if (!token) return;
    fetchNotifications(token).then((list) => {
      if (!list.length) return;
      const isRu = !i18n.language?.startsWith("en");
      for (const n of list) {
        const lesson = isRu ? n.lesson_title_ru : (n.lesson_title_en || n.lesson_title_ru);
        const note = n.note?.trim() || undefined;
        if (n.kind === "work_passed") {
          pushAchievement({ id: n.id, variant: "success", title: t("achv.passedTitle"), message: lesson || t("achv.passedMsg"), note });
        } else {
          pushAchievement({ id: n.id, variant: "retry", title: t("achv.retryTitle"), message: (lesson ? `${lesson}. ` : "") + t("achv.retryMsg"), note });
        }
      }
      ackNotifications(list.map((n) => n.id), token);
    }).catch(() => { /* best-effort */ });
  }, [token, i18n.language, t, pushAchievement]);

  useEffect(() => { if (me) checkNotifications(); }, [me, checkNotifications]);
  useEffect(() => { if (view === "menu") checkNotifications(); }, [view, checkNotifications]);

  // Redeem a pending invite as soon as there is an account to put it on — a
  // brand-new one straight after sign-up, or an existing one. Any answer the
  // server names (used, expired, …) is final and drops the code; a network
  // failure keeps it for the next launch.
  useEffect(() => {
    if (!token || !me || !inviteCode || redeemingRef.current === inviteCode) return;
    redeemingRef.current = inviteCode;
    const isRu = !i18n.language?.startsWith("en");
    redeemInvite(inviteCode, token)
      .then((res) => {
        const names = res.blocks.map((b) => (isRu ? b.title_ru : b.title_en)).join(", ");
        pushAchievement({
          id: `invite-${inviteCode}`,
          variant: "success",
          title: t(res.outcome === "ok" ? "invite.okTitle" : "invite.alreadyTitle"),
          message: t("invite.okMessage", { blocks: names, count: res.days }),
        });
        clearInvite();
        setInviteCode(null);
        fetchSubscription(token).then(setSubscription).catch(() => {});
      })
      .catch((e) => {
        const code = inviteErrorCode(e);
        if (!code) { redeemingRef.current = null; return; }
        pushAchievement({
          id: `invite-${inviteCode}`,
          variant: code === "INVITE_ADMIN" ? "info" : "retry",
          title: t("invite.failTitle"),
          message: t(`invite.err.${code}`, { defaultValue: t("invite.err.generic") }),
        });
        clearInvite();
        setInviteCode(null);
      });
  }, [token, me, inviteCode, i18n.language, t, pushAchievement]);

  const themeProps = { theme, onThemeChange: setTheme };

  // "See plans" buttons open the plans page from a lesson, the calculator,
  // history or the chat; back goes to wherever the student came from.
  const returnFromAccount = () => {
    if (accountReturnTo) {
      const back = accountReturnTo;
      setAccountReturnTo(null);
      // Coming back to the list, not to a lesson we were sent away from.
      if (back === "study") setStudyOpenId(null);
      setView(back);
      return;
    }
    setView("menu");
  };

  let screen: React.ReactNode;

  if (handoffPending && handoffWho) {
    screen = (
      <div className="handoff-confirm">
        <div className="glass glass-lg handoff-confirm__card">
          <p className="eyebrow" style={{ margin: 0 }}>{t("handoff.confirmEyebrow")}</p>
          <h2 className="t-title" style={{ margin: "6px 0 8px" }}>{t("handoff.confirmTitle")}</h2>
          <p className="t-sm" style={{ margin: 0 }}>
            {t("handoff.confirmText")} <b>{handoffWho.name}</b>{handoffWho.username ? ` (@${handoffWho.username})` : ""}
          </p>
          {token && <p className="t-xs muted-2" style={{ margin: "8px 0 0", lineHeight: 1.45 }}>{t("handoff.confirmReplace")}</p>}
          <p className="t-xs muted-2" style={{ margin: "8px 0 14px", lineHeight: 1.45 }}>{t("handoff.confirmWarn")}</p>
          <button className="btn btn-primary btn-block" onClick={acceptHandoff}>{t("handoff.confirmYes")}</button>
          <button className="btn btn-glass btn-block" style={{ marginTop: 8 }} onClick={declineHandoff}>{t("handoff.confirmNo")}</button>
        </div>
      </div>
    );
  } else if (handoffPending) {
    screen = <BeeSpinner />;
  } else if (!token) {
    const inTelegram = !!WebApp.initData?.trim();
    if (inTelegram && !loggedOut) {
      screen = <BeeSpinner />;
    } else {
      screen = (
        <LoginView
          onLoginFirebase={handleLoginFirebase}
          onToken={persistToken}
          inTelegram={inTelegram}
          onTelegramLogin={handleTelegramLogin}
          inviteCode={inviteCode}
          {...themeProps}
        />
      );
    }
  } else if (!me) {
    screen = <BeeSpinner />;
  } else if (approveCode) {
    screen = (
      <ApproveLogin
        code={approveCode}
        token={token}
        accountName={[me.first_name, me.last_name].filter(Boolean).join(" ") || me.username || me.email || "—"}
        onClose={closeApprove}
        onSwitchToGoogle={logout}
        onToken={persistToken}
      />
    );
  } else if (view === "selection") {
    screen = <SelectionWizard token={token} onExit={() => setView("menu")}
      cached={selCache}
      onCache={setSelCache}
      onOpenLesson={(lessonId) => {
        setStudyOpenId(lessonId);
        setLessonReturnsTo("selection");
        setView("study");
      }}
      onOpenPlans={() => { setAccountReturnTo("selection"); setView("subscription"); }} {...themeProps} />;
  } else if (view === "study") {
    screen = (
      <StudyView
        token={token}
        onExit={() => setView("menu")}
        initialOpenId={studyOpenId}
        onBackFromLesson={lessonReturnsTo
          ? () => { const back = lessonReturnsTo; setLessonReturnsTo(null); setStudyOpenId(null); setView(back); }
          : null}
        onAsk={(subject, lessonId) => { setChatCtx({ subject, lessonId }); setView("chat"); }}
        onOpenPlans={(section) => {
          setStudyOpenSection(section ?? null);
          setAccountReturnTo("study");
          setView("subscription");
        }}
        initialOpenSection={studyOpenSection}
        isAdmin={me.role === "admin"}
        onQuizStats={() => setView("quizstats")}
        onAchievement={pushAchievement}
        {...themeProps}
      />
    );
  } else if (view === "history") {
    screen = <HistoryView token={token} onExit={() => setView("menu")}
      onOpenLesson={(lessonId) => {
        setStudyOpenId(lessonId);
        setLessonReturnsTo("history");
        setView("study");
      }}
      onOpenPlans={() => { setAccountReturnTo("history"); setView("subscription"); }}
      {...themeProps} />;
  } else if (view === "games") {
    screen = <GamesHub token={token} onExit={() => setView("menu")} {...themeProps} />;
  } else if (view === "quizstats") {
    screen = <AdminQuizStats token={token} onExit={() => setView("study")} {...themeProps} />;
  } else if (view === "chat") {
    screen = (
      <ChatScreen
        token={token}
        onExit={() => { setChatCtx(null); setView("menu"); }}
        subject={chatCtx?.subject}
        onOpenPlans={() => { setAccountReturnTo("chat"); setView("subscription"); }}
        onReturn={chatCtx ? () => { setStudyOpenId(chatCtx.lessonId); setChatCtx(null); setLessonReturnsTo(null); setView("study"); } : undefined}
        {...themeProps}
      />
    );
  } else if (view === "adminchats") {
    screen = <AdminChats token={token} onExit={() => setView("menu")} {...themeProps} />;
  } else if (view === "taskreview") {
    screen = <AdminTaskReview token={token} onExit={() => setView("menu")} {...themeProps} />;
  } else if (view === "adminsettings") {
    screen = <AdminSettings token={token} onExit={() => setView("menu")} {...themeProps} />;
  } else if (view === "admininvites") {
    screen = <AdminInvites token={token} onExit={() => setView("menu")} {...themeProps} />;
  } else if (view === "adminusers") {
    screen = <AdminUsers token={token} onExit={() => setView("menu")} {...themeProps} />;
  } else if (view === "subscription") {
    screen = (
      <AccountView
        token={token}
        focus="subscription"
        autoBuyPlanId={autoBuyPlanId}
        onAutoBuyDone={() => setAutoBuyPlanId(null)}
        onExit={returnFromAccount}
        onLogout={logout}
        {...themeProps}
      />
    );
  } else if (view === "account") {
    screen = (
      <AccountView
        token={token}
        badges={badges}
        onChat={() => { setChatCtx(null); setView("chat"); }}
        onTaskReview={() => setView("taskreview")}
        onAdminChats={() => setView("adminchats")}
        onAdminSettings={() => setView("adminsettings")}
        onExit={returnFromAccount}
        onLogout={logout}
        {...themeProps}
      />
    );
  } else {
    screen = (
      <MainMenu
        token={token}
        me={me}
        subscription={subscription}
        onStartSelection={() => setView("selection")}
        onStudy={() => { setStudyOpenId(null); setStudyOpenSection(null); setLessonReturnsTo(null); setView("study"); }}
        continueLesson={continueLesson}
        onContinue={(id) => { setStudyOpenId(id); setStudyOpenSection(null); setLessonReturnsTo(null); setView("study"); }}
        onHistory={() => setView("history")}
        onGames={() => setView("games")}
        onAdminSettings={() => setView("adminsettings")}
        onAdminInvites={() => setView("admininvites")}
        onAdminUsers={() => setView("adminusers")}
        badges={badges}
        onSubscription={() => setView("subscription")}
        isAdmin={me.role === "admin"}
        onAccount={() => setView("account")}
        onLogout={logout}
        {...themeProps}
      />
    );
  }

  return (
    <div
      ref={rootRef}
      className={"beeapp" + (animOn ? " anim-on" : "")}
      data-theme={theme}
      data-device={device}
    >
      <div className="beeapp__bg">
        <div className="beeapp__bg-grain" />
      </div>
      <AppConfigProvider token={token}>
        <MaintenanceGate
          ready={!!me}
          isAdmin={me?.role === "admin"}
          theme={theme}
          onThemeChange={setTheme}
        >
          {screen}
        </MaintenanceGate>
        <AchievementOverlay items={achievements} onDismiss={dismissAchievement} theme={theme} />
      </AppConfigProvider>
    </div>
  );
}
